import React, { useState } from 'react';
import { Button, Modal, Spin, Result, Typography, message } from 'antd';
import { FileTextOutlined, CheckCircleOutlined, LoadingOutlined, SwapOutlined } from '@ant-design/icons';
import { pdf } from '@react-pdf/renderer';
import { InspectionRequest } from '@/services/types/inspection.types';
import { AutomaticReportResponse } from '@/services/aiAnalysis.service';
import { LaudoDocument } from '../../pdf/LaudoDocument';
import { LaudoData } from '../../types';
import { inspectionReportService } from '@/services/inspectionReport.service';
import { transformAiReportToLaudoData } from '../../utils/transformAiReportToLaudoData';
import { transformAiReportToWizardData } from '../../utils/transformLaudoData';
import { refreshLaudoPhotos, getBariLogoBase64 } from '../../pdf/utils/imageUtils';
import { streetViewService } from '@/services/streetview.service';

const { Text, Paragraph } = Typography;

interface StreetViewOption {
  heading: number;
  base64: string;
  label: string;
}

interface AutomaticLaudoGeneratorProps {
  inspection: InspectionRequest;
  aiReportData: AutomaticReportResponse;
  disabled?: boolean;
  onSuccess?: (() => void) | undefined;
  onError?: ((error: string) => void) | undefined;
}

type GenerationStage = 'idle' | 'preparing' | 'generating_pdf' | 'saving' | 'completed' | 'error';

export const AutomaticLaudoGenerator: React.FC<AutomaticLaudoGeneratorProps> = ({
  inspection,
  aiReportData,
  disabled,
  onSuccess,
  onError,
}) => {
  const [stage, setStage] = useState<GenerationStage>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [streetViewOptions, setStreetViewOptions] = useState<StreetViewOption[]>([]);
  const [selectedHeading, setSelectedHeading] = useState<number | null>(null);
  const [laudoDataState, setLaudoDataState] = useState<LaudoData | null>(null);
  const [logoBase64State, setLogoBase64State] = useState<string | undefined>(undefined);
  const [reportId, setReportId] = useState<string | null>(null);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [swapping, setSwapping] = useState(false);

  const buildPdfBlob = async (
    laudoData: LaudoData,
    logoBase64: string | undefined,
    streetViewBase64: string | undefined,
  ): Promise<Blob> => {
    const pdfDocument = (
      <LaudoDocument
        data={laudoData}
        logoBase64={logoBase64}
        isAutomatic={true}
        streetViewImageBase64={streetViewBase64}
      />
    );
    return pdf(pdfDocument).toBlob();
  };

  const generateAutomaticPdf = async () => {
    setShowModal(true);
    setStage('preparing');
    setErrorMessage(null);

    try {
      setStage('preparing');
      const laudoDataRaw: LaudoData = transformAiReportToLaudoData(aiReportData, inspection);

      const laudoData = await refreshLaudoPhotos(laudoDataRaw) as LaudoData;

      let images: StreetViewOption[] = [];
      let initialBase64: string | undefined;
      let initialHeading: number | null = null;
      try {
        const firstItem = inspection.items?.[0];
        if (firstItem?.street && firstItem?.city) {
          const street = firstItem.street;
          const number = firstItem.number || 'S/N';
          const district = firstItem.district || '';
          const city = firstItem.city;
          const state = firstItem.state || '';
          const cep = firstItem.cep || '';

          const endereco = `${street}, ${number}, ${district}, ${city}/${state}, ${cep}`;
          console.log('[AVM] Buscando Street View para:', endereco);

          images = await streetViewService.fetchMultipleStreetViewAngles(endereco);
          console.log('[AVM] Street View retornou', images.length, 'imagens');
          if (images.length > 0) {
            initialBase64 = images[0].base64;
            initialHeading = images[0].heading;
          }
        } else {
          console.warn('[AVM] Endereço incompleto, pulando Street View');
        }
      } catch (e) {
        console.warn('[AVM] Não foi possível obter imagem do Street View:', e);
      }
      setStreetViewOptions(images);
      setSelectedHeading(initialHeading);

      setStage('generating_pdf');
      let logoBase64: string | undefined;
      try {
        logoBase64 = await getBariLogoBase64();
      } catch {
        logoBase64 = undefined;
      }
      setLogoBase64State(logoBase64);
      setLaudoDataState(laudoData);

      const pdfBlob = await buildPdfBlob(laudoData, logoBase64, initialBase64);

      const url = URL.createObjectURL(pdfBlob);
      setPdfUrl(url);

      setStage('saving');
      const itemId = inspection.items?.[0]?.id;
      if (!itemId) {
        throw new Error('ID do item de inspeção não encontrado');
      }

      const wizardData = transformAiReportToWizardData(aiReportData, inspection);

      const wizardDataToSave = {
        version: '1.0',
        isAutomaticReport: true,
        aiAnalysisJobId: aiReportData.aiAnalysisJobId,
        wizardData: wizardData,
        generatedAt: new Date().toISOString(),
        streetViewHeading: initialHeading,
        inspection: {
          id: inspection.id,
          proposalNumber: inspection.proposalNumber,
        },
      };

      const createdReport = await inspectionReportService.createReport({
        inspectionItemId: itemId,
        documentId: '',
        fileName: '',
        fileSize: 0,
        fileType: 'application/pdf',
        filePath: '',
        reportData: JSON.stringify(wizardDataToSave),
        notes: 'Laudo gerado automaticamente via Inteligência Artificial — aguardando aprovação do banco',
      });
      setReportId(createdReport.id);

      setStage('completed');
      onSuccess?.();

    } catch (error: any) {
      setStage('error');
      setErrorMessage(error.message || 'Erro desconhecido ao gerar o laudo');
      onError?.(error.message || 'Erro desconhecido');
    }
  };

  const handleClose = () => {
    setShowModal(false);
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  const handleViewPdf = () => {
    if (pdfUrl) {
      window.open(pdfUrl, '_blank');
    }
  };

  const handleOpenSwap = () => {
    setShowSwapModal(true);
  };

  const handleSelectStreetView = async (option: StreetViewOption) => {
    if (!laudoDataState) return;
    if (option.heading === selectedHeading) {
      setShowSwapModal(false);
      return;
    }

    setSwapping(true);
    try {
      const pdfBlob = await buildPdfBlob(laudoDataState, logoBase64State, option.base64);

      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      const newUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(newUrl);
      setSelectedHeading(option.heading);

      const itemId = inspection.items?.[0]?.id;
      if (itemId && reportId) {
        const wizardData = transformAiReportToWizardData(aiReportData, inspection, undefined, option.base64);
        const wizardDataToSave = {
          version: '1.0',
          isAutomaticReport: true,
          aiAnalysisJobId: aiReportData.aiAnalysisJobId,
          wizardData: wizardData,
          generatedAt: new Date().toISOString(),
          streetViewHeading: option.heading,
          inspection: {
            id: inspection.id,
            proposalNumber: inspection.proposalNumber,
          },
        };

        try {
          await inspectionReportService.deleteReport(reportId);
        } catch (err) {
          console.warn('[AVM] Falha ao remover laudo anterior antes de regerar:', err);
        }

        const created = await inspectionReportService.createReport({
          inspectionItemId: itemId,
          documentId: '',
          fileName: '',
          fileSize: 0,
          fileType: 'application/pdf',
          filePath: '',
          reportData: JSON.stringify(wizardDataToSave),
          notes: `Laudo gerado automaticamente via Inteligência Artificial — foto de fachada: ${option.label} (${option.heading}°)`,
        });
        setReportId(created.id);
      }

      message.success(`Foto da fachada atualizada para ${option.label} (${option.heading}°)`);
      setShowSwapModal(false);
    } catch (e: any) {
      message.error(e?.message || 'Erro ao trocar a foto da fachada');
    } finally {
      setSwapping(false);
    }
  };

  const getStageDescription = () => {
    switch (stage) {
      case 'preparing':
        return 'Preparando dados do laudo...';
      case 'generating_pdf':
        return 'Gerando PDF do laudo...';
      case 'saving':
        return 'Salvando laudo no sistema...';
      case 'completed':
        return 'Laudo gerado com sucesso!';
      case 'error':
        return 'Erro ao gerar laudo';
      default:
        return '';
    }
  };

  const canSwapStreetView = streetViewOptions.length > 1;

  const renderModalContent = () => {
    if (stage === 'completed') {
      const successExtra: React.ReactNode[] = [];
      if (pdfUrl) {
        successExtra.push(
          <Button
            key="view"
            type="primary"
            icon={<FileTextOutlined />}
            onClick={handleViewPdf}
          >
            Visualizar PDF
          </Button>
        );
      }
      if (canSwapStreetView) {
        successExtra.push(
          <Button
            key="swap"
            icon={<SwapOutlined />}
            onClick={handleOpenSwap}
          >
            Trocar Foto da Fachada
          </Button>
        );
      }
      successExtra.push(
        <Button key="close" onClick={handleClose}>
          Fechar
        </Button>
      );

      return (
        <Result
          status="success"
          title="Laudo Automático Gerado!"
          subTitle="O laudo está aguardando aprovação do banco. O PDF será arquivado no sistema após a aprovação."
          extra={successExtra}
        />
      );
    }

    if (stage === 'error') {
      return (
        <Result
          status="error"
          title="Erro ao Gerar Laudo"
          subTitle={errorMessage || 'Ocorreu um erro durante a geração do laudo.'}
          extra={[
            <Button key="retry" type="primary" onClick={generateAutomaticPdf}>
              Tentar Novamente
            </Button>,
            <Button key="close" onClick={handleClose}>
              Fechar
            </Button>,
          ]}
        />
      );
    }

    return (
      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        <Spin
          indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />}
          size="large"
        />
        <Paragraph style={{ marginTop: 24, fontSize: 16 }}>
          {getStageDescription()}
        </Paragraph>
        <div style={{ marginTop: 16 }}>
          <Text type="secondary">
            {stage === 'preparing' && 'Transformando dados da análise de IA...'}
            {stage === 'generating_pdf' && 'Renderizando documento PDF...'}
            {stage === 'saving' && 'Registrando laudo no sistema...'}
          </Text>
        </div>
      </div>
    );
  };

  return (
    <>
      <Button
        type="primary"
        size="large"
        icon={<FileTextOutlined />}
        onClick={generateAutomaticPdf}
        disabled={disabled}
        style={{ minWidth: 200 }}
      >
        Gerar PDF do Laudo
      </Button>

      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {stage === 'completed' ? (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            ) : (
              <FileTextOutlined style={{ color: '#1890ff' }} />
            )}
            <span>Geração Automática do Laudo</span>
          </div>
        }
        open={showModal}
        onCancel={handleClose}
        footer={null}
        closable={stage === 'completed' || stage === 'error' || stage === 'idle'}
        maskClosable={false}
        width={500}
      >
        {renderModalContent()}
      </Modal>

      <Modal
        title="Selecione a melhor foto da fachada"
        open={showSwapModal}
        onCancel={() => !swapping && setShowSwapModal(false)}
        footer={null}
        width={720}
        maskClosable={!swapping}
        closable={!swapping}
      >
        <Paragraph type="secondary" style={{ marginBottom: 16 }}>
          Clique sobre a foto para selecioná-la. O PDF será regerado com a direção escolhida.
        </Paragraph>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 16,
            opacity: swapping ? 0.6 : 1,
            pointerEvents: swapping ? 'none' : 'auto',
          }}
        >
          {streetViewOptions.map((option) => {
            const isSelected = option.heading === selectedHeading;
            return (
              <div
                key={option.heading}
                onClick={() => handleSelectStreetView(option)}
                style={{
                  border: isSelected ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  borderRadius: 8,
                  padding: 8,
                  cursor: 'pointer',
                  background: isSelected ? '#e6f7ff' : '#fff',
                  transition: 'all 0.2s ease',
                }}
              >
                <img
                  src={option.base64}
                  alt={option.label}
                  style={{
                    width: '100%',
                    height: 180,
                    objectFit: 'cover',
                    borderRadius: 4,
                  }}
                />
                <div style={{ marginTop: 8, textAlign: 'center' }}>
                  <Text strong>
                    {option.label} ({option.heading}°)
                  </Text>
                  {isSelected && (
                    <div>
                      <Text type="success" style={{ fontSize: 12 }}>
                        <CheckCircleOutlined /> Selecionada
                      </Text>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {swapping && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Spin /> <Text type="secondary">Regerando PDF...</Text>
          </div>
        )}
      </Modal>
    </>
  );
};

export default AutomaticLaudoGenerator;
